import { useState, useEffect, useCallback, useMemo } from 'react';
import AdminLayout from '@/components/feature/AdminLayout';
import { supabase } from '@/lib/supabaseClient';
import { DURUM_RENKLERI, TODAY } from '@/lib/constants';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell, BarChart, Bar,
} from 'recharts';

interface StatCardProps {
  title: string; value: string | number; icon: string; trend?: string; trendUp?: boolean; color?: string;
}
function StatCard({ title, value, icon, trend, trendUp, color = 'vip-gold' }: StatCardProps) {
  return (
    <div className="bg-vip-dark border border-vip-gold/10 rounded-lg p-4 md:p-5">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs text-gray-400 mb-1">{title}</p>
          <p className="text-xl md:text-2xl font-bold text-white">{value}</p>
          {trend && (
            <p className={`text-xs mt-1 ${trendUp ? 'text-emerald-400' : 'text-red-400'}`}>
              <i className={trendUp ? 'ri-arrow-up-line' : 'ri-arrow-down-line'}></i>{trend}
            </p>
          )}
        </div>
        <div className={`w-9 h-9 rounded-lg bg-${color}/15 border border-${color}/20 flex items-center justify-center flex-shrink-0`}>
          <i className={`${icon} text-${color} text-sm`}></i>
        </div>
      </div>
    </div>
  );
}

export default function DashboardPage() {
  const [mounted, setMounted] = useState(false);
  const [currentTime, setCurrentTime] = useState('');
  const [loading, setLoading] = useState(true);
  const [todayRez, setTodayRez] = useState<any[]>([]);
  const [upcomingRez, setUpcomingRez] = useState<any[]>([]);
  const [allRez, setAllRez] = useState<any[]>([]);
  const [vehicles, setVehicles] = useState<any[]>([]);
  const [drivers, setDrivers] = useState<any[]>([]);
  const [hata, setHata] = useState('');

  useEffect(() => {
    setMounted(true);
    const updateTime = () => setCurrentTime(new Date().toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' }));
    updateTime();
    const interval = setInterval(updateTime, 60000);
    return () => clearInterval(interval);
  }, []);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setHata('');
    const weekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
    const monthAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
    const [todayRes, upcomRes, allRes, vRes, dRes] = await Promise.all([
      supabase.from('reservations').select('id,rezervasyon_no,musteri_adi,gelis_saati,ucak_kodu,alinis_yeri,birak_yeri,ucret,para_birimi,durum,ilce,kisi_sayisi,sofor,atanan_arac,atanan_plaka').eq('gelis_tarihi', TODAY).order('gelis_saati'),
      supabase.from('reservations').select('id,rezervasyon_no,musteri_adi,gelis_tarihi,gelis_saati,ilce,kisi_sayisi,durum,ucret,para_birimi').gt('gelis_tarihi', TODAY).neq('durum','İptal Edildi').order('gelis_tarihi').limit(6),
      supabase.from('reservations').select('gelis_tarihi,ucret,para_birimi,durum').gte('gelis_tarihi', monthAgo),
      supabase.from('vehicles').select('durum,ad').order('ad'),
      supabase.from('drivers').select('id,ad_soyad,telefon').eq('aktif', true).order('ad_soyad'),
    ]);
    if (todayRes.error) setHata(todayRes.error.message);
    if (upcomRes.error) setHata(upcomRes.error.message);
    if (allRes.error) setHata(allRes.error.message);
    if (vRes.error) setHata(vRes.error.message);
    if (dRes.error) setHata(dRes.error.message);

    setTodayRez(todayRes.data || []);
    setUpcomingRez(upcomRes.data || []);
    setAllRez(allRes.data || []);
    setVehicles(vRes.data || []);
    setDrivers(dRes.data || []);
    setLoading(false);
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const activeAll = allRez.filter(r => r.durum !== 'İptal Edildi');
  const todayLabel = new Date().toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric', weekday: 'long' });
  const weekStart = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];

  const gunlukEUR = todayRez.filter(r => r.para_birimi === 'EUR' && r.durum !== 'İptal Edildi').reduce((s: number, r: any) => s + Number(r.ucret), 0);
  const gunlukUSD = todayRez.filter(r => r.para_birimi === 'USD' && r.durum !== 'İptal Edildi').reduce((s: number, r: any) => s + Number(r.ucret), 0);
  const gunlukTL = todayRez.filter(r => r.para_birimi === 'TL' && r.durum !== 'İptal Edildi').reduce((s: number, r: any) => s + Number(r.ucret), 0);
  const toplamEUR = activeAll.filter(r => r.para_birimi === 'EUR').reduce((s: number, r: any) => s + Number(r.ucret), 0);
  const toplamUSD = activeAll.filter(r => r.para_birimi === 'USD').reduce((s: number, r: any) => s + Number(r.ucret), 0);
  const toplamTL = activeAll.filter(r => r.para_birimi === 'TL').reduce((s: number, r: any) => s + Number(r.ucret), 0);

  const haftalikRez = allRez.filter(r => r.gelis_tarihi >= weekStart && r.durum !== 'İptal Edildi').length;

  // Daily chart data (last 7 days)
  const chartData = (() => {
    const days: Record<string, { tarih: string; EUR: number; USD: number }> = {};
    for (let i = 6; i >= 0; i--) {
      const d = new Date(Date.now() - i * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
      const label = new Date(d).toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' });
      days[d] = { tarih: label, EUR: 0, USD: 0 };
    }
    allRez.filter(r => r.durum !== 'İptal Edildi').forEach((r: any) => {
      if (days[r.gelis_tarihi]) {
        if (r.para_birimi === 'EUR') days[r.gelis_tarihi].EUR += Number(r.ucret);
        if (r.para_birimi === 'USD') days[r.gelis_tarihi].USD += Number(r.ucret);
      }
    });
    return Object.values(days);
  })();

  const currencyData = [
    { name: 'EUR', value: toplamEUR, color: '#C8A45C' },
    { name: 'USD', value: toplamUSD, color: '#60A5FA' },
    { name: 'TL', value: toplamTL, color: '#34D399' },
  ].filter(d => d.value > 0);

  const vehicleStats = {
    musait: vehicles.filter(v => v.durum === 'Müsait').length,
    gorevde: vehicles.filter(v => v.durum === 'Görevde').length,
    bakimda: vehicles.filter(v => v.durum === 'Bakımda').length,
    toplam: vehicles.length,
  };

  // Şoför bazlı bugün özetini hesapla
  const soforOzet = useMemo(() => {
    const carpan: Record<string, number> = { EUR: 38, USD: 35, GBP: 44, TL: 1 };
    return drivers.map(d => {
      const isler = todayRez.filter(r => r.sofor === d.ad_soyad && r.durum !== 'İptal Edildi');
      const toplam = isler.reduce((s, r) => {
        const pb = r.para_birimi || 'TL';
        const k = carpan[pb] || 1;
        return s + (Number(r.ucret) || 0) * k;
      }, 0);
      return { driver: d, isSayisi: isler.length, toplamTL: toplam };
    }).sort((a, b) => b.isSayisi - a.isSayisi);
  }, [drivers, todayRez]);

  const tooltipStyle = { backgroundColor: '#1F2937', border: '1px solid rgba(200,164,92,0.2)', borderRadius: '8px', color: '#fff' };

  return (
    <AdminLayout>
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-xl md:text-2xl font-display font-bold text-white">Dashboard</h2>
          <p className="text-sm text-gray-400 mt-1">VIP Antalya Transfer - Operasyon Özeti</p>
        </div>
        <a href="/rezervasyonlar/yeni" className="px-4 py-2 bg-vip-gold text-vip-black text-sm font-semibold rounded-md hover:bg-vip-gold-light transition-colors whitespace-nowrap text-center">
          <i className="ri-add-line mr-1"></i>Yeni Rezervasyon
        </a>
      </div>

      <div className="bg-gradient-to-r from-vip-gold/10 to-transparent border border-vip-gold/10 rounded-lg px-4 py-3 mb-6 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-vip-gold/20 flex items-center justify-center"><i className="ri-time-line text-vip-gold text-sm"></i></div>
          <span className="text-sm text-gray-300">
            Bugün: <span className="text-white font-semibold">{todayLabel}</span> — Saat: <span className="text-vip-gold font-semibold">{currentTime}</span>
          </span>
        </div>
        <div className="hidden md:flex items-center gap-2">
          <span className="text-xs text-emerald-400 bg-emerald-500/10 px-2 py-1 rounded-md border border-emerald-500/20">
            <i className="ri-circle-fill text-[6px] mr-1"></i>Sistem Aktif
          </span>
        </div>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 mb-6">
          {[...Array(6)].map((_, i) => <div key={i} className="h-20 bg-vip-dark rounded-lg animate-pulse"></div>)}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 mb-6">
            <StatCard title="Bugünkü Rezervasyon" value={todayRez.length} icon="ri-calendar-check-line" />
            <StatCard title="Haftalık Rezervasyon" value={haftalikRez} icon="ri-calendar-event-line" />
            <StatCard title="Aylık Rezervasyon" value={activeAll.length} icon="ri-calendar-todo-line" />
            <StatCard title="Bugünkü Kazanç (EUR)" value={`${gunlukEUR} €`} icon="ri-money-euro-circle-line" color="emerald-400" />
            <StatCard title="Bugünkü Kazanç (USD)" value={`${gunlukUSD} $`} icon="ri-money-dollar-circle-line" color="blue-400" />
            <StatCard title="Bugünkü Kazanç (TL)" value={`${gunlukTL.toLocaleString('tr-TR')} ₺`} icon="ri-money-cny-circle-line" color="green-400" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
            <StatCard title="Toplam Ciro (EUR)" value={`${toplamEUR.toLocaleString('tr-TR')} €`} icon="ri-bank-card-line" color="vip-gold" />
            <StatCard title="Toplam Ciro (USD)" value={`${toplamUSD.toLocaleString('tr-TR')} $`} icon="ri-bank-card-2-line" color="blue-400" />
            <StatCard title="Toplam Ciro (TL)" value={`${toplamTL.toLocaleString('tr-TR')} ₺`} icon="ri-bank-line" color="green-400" />
          </div>
        </>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <div className="bg-vip-dark border border-vip-gold/10 rounded-lg p-5">
          <h3 className="text-sm font-semibold text-white mb-4">Günlük Kazanç Grafiği (Son 7 Gün)</h3>
          {mounted && !loading && (
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#2D3748" />
                <XAxis dataKey="tarih" stroke="#6B7280" fontSize={12} />
                <YAxis stroke="#6B7280" fontSize={12} />
                <Tooltip contentStyle={tooltipStyle} />
                <Legend wrapperStyle={{ fontSize: '12px' }} />
                <Line type="monotone" dataKey="EUR" stroke="#C8A45C" strokeWidth={2} dot={{ fill: '#C8A45C', r: 3 }} />
                <Line type="monotone" dataKey="USD" stroke="#60A5FA" strokeWidth={2} dot={{ fill: '#60A5FA', r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="bg-vip-dark border border-vip-gold/10 rounded-lg p-5">
          <h3 className="text-sm font-semibold text-white mb-4">Para Birimi Dağılımı</h3>
          {mounted && !loading && currencyData.length > 0 && (
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie data={currencyData} cx="50%" cy="50%" innerRadius={60} outerRadius={90} paddingAngle={4} dataKey="value">
                  {currencyData.map((entry, index) => <Cell key={index} fill={entry.color} />)}
                </Pie>
                <Tooltip contentStyle={tooltipStyle} />
                <Legend wrapperStyle={{ fontSize: '12px' }} />
              </PieChart>
            </ResponsiveContainer>
          )}
          {!loading && currencyData.length === 0 && <p className="text-sm text-gray-500 text-center py-10">Veri yok</p>}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <div className="bg-vip-dark border border-vip-gold/10 rounded-lg p-5">
          <h3 className="text-sm font-semibold text-white mb-4">Araç Durum Özeti</h3>
          <div className="space-y-4">
            {[
              { label: 'Müsait', count: vehicleStats.musait, color: 'emerald' },
              { label: 'Görevde', count: vehicleStats.gorevde, color: 'blue' },
              { label: 'Bakımda', count: vehicleStats.bakimda, color: 'red' },
            ].map(s => (
              <div key={s.label}>
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-3">
                    <div className={`w-3 h-3 rounded-full bg-${s.color}-500`}></div>
                    <span className="text-sm text-gray-300">{s.label}</span>
                  </div>
                  <span className={`text-lg font-bold text-${s.color}-400`}>{s.count}</span>
                </div>
                <div className="w-full bg-vip-surface rounded-full h-2">
                  <div className={`bg-${s.color}-500 h-2 rounded-full`} style={{ width: vehicleStats.toplam > 0 ? `${(s.count / vehicleStats.toplam) * 100}%` : '0%' }}></div>
                </div>
              </div>
            ))}
            <div className="pt-3 border-t border-vip-gold/10 flex items-center justify-between">
              <span className="text-sm text-gray-400">Toplam Araç</span>
              <span className="text-lg font-bold text-vip-gold">{vehicleStats.toplam}</span>
            </div>
          </div>
        </div>

        <div className="bg-vip-dark border border-vip-gold/10 rounded-lg p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-white">Bugünkü Transferler</h3>
            <a href="/bugun" className="text-xs text-vip-gold hover:text-vip-gold-light transition-colors">Tümünü Gör <i className="ri-arrow-right-line ml-0.5"></i></a>
          </div>
          <div className="space-y-2 max-h-72 overflow-y-auto">
            {todayRez.slice(0, 8).map(r => (
              <div key={r.id} className="flex items-center gap-3 p-3 rounded-lg bg-vip-surface/50 border border-vip-gold/5 hover:border-vip-gold/15 transition-colors">
                <div className="flex-shrink-0 w-12 text-center">
                  <p className="text-xs text-vip-gold font-semibold">{r.gelis_saati?.slice(0,5)}</p>
                  <p className="text-[10px] text-gray-500">{r.ucak_kodu}</p>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-white font-medium truncate">{r.musteri_adi}</p>
                  <p className="text-xs text-gray-400 truncate">{r.alinis_yeri} → {r.birak_yeri}</p>
                </div>
                <div className="flex items-center gap-1">
                  <span className={`text-[10px] px-2 py-1 rounded-md border ${DURUM_RENKLERI[r.durum] || 'bg-gray-500/20 text-gray-400 border-gray-500/30'}`}>{r.durum}</span>
                  <a href={`/rezervasyonlar/yeni?edit=${r.id}`} className="w-6 h-6 rounded-md hover:bg-vip-gold/10 text-gray-600 hover:text-vip-gold transition-colors flex items-center justify-center"><i className="ri-edit-line text-[10px]"></i></a>
                </div>
              </div>
            ))}
            {todayRez.length === 0 && <p className="text-sm text-gray-500 text-center py-4">Bugün transfer yok</p>}
          </div>
        </div>
      </div>

      {/* Şoför Özeti */}
      <div className="bg-vip-dark border border-vip-gold/10 rounded-lg p-5 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-white">Şoför Özeti — Bugün</h3>
          <a href="/sofor-hesap" className="text-xs text-vip-gold hover:text-vip-gold-light transition-colors">Detaylı Gör <i className="ri-arrow-right-line ml-0.5"></i></a>
        </div>
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
            {[...Array(4)].map((_, i) => <div key={i} className="h-20 bg-vip-surface/50 rounded-lg animate-pulse"></div>)}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
            {soforOzet.map((s) => (
              <div key={s.driver.id} className="bg-vip-black/50 border border-vip-gold/10 rounded-lg p-3 hover:border-vip-gold/20 transition-colors">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-8 h-8 rounded-full bg-vip-gold/15 border border-vip-gold/20 flex items-center justify-center text-vip-gold font-semibold text-xs flex-shrink-0">
                    {s.driver.ad_soyad.split(' ').map((n: string) => n[0]).join('').slice(0, 2).toUpperCase()}
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-white truncate">{s.driver.ad_soyad}</p>
                    <p className="text-[10px] text-gray-500">{s.driver.telefon}</p>
                  </div>
                </div>
                <div className="flex items-end justify-between">
                  <div>
                    <p className="text-[10px] text-gray-500">İş</p>
                    <p className="text-base font-bold text-white">{s.isSayisi}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-[10px] text-gray-500">Toplam</p>
                    <p className="text-base font-bold text-vip-gold">{s.toplamTL.toLocaleString('tr-TR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} ₺</p>
                  </div>
                </div>
              </div>
            ))}
            {soforOzet.length === 0 && (
              <div className="col-span-full text-center py-6 text-gray-500 text-sm">
                <i className="ri-steering-line text-xl mb-2 block"></i>
                Aktif şoför bulunamadı.
              </div>
            )}
          </div>
        )}
      </div>

      <div className="bg-vip-dark border border-vip-gold/10 rounded-lg p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-white">Yaklaşan Rezervasyonlar</h3>
          <a href="/yaklasan" className="text-xs text-vip-gold hover:text-vip-gold-light transition-colors">Tümünü Gör <i className="ri-arrow-right-line ml-0.5"></i></a>
        </div>
        <div className="space-y-2">
          {upcomingRez.slice(0, 6).map(r => (
            <div key={r.id} className="flex items-center gap-3 p-3 rounded-lg bg-vip-surface/50 border border-vip-gold/5 hover:border-vip-gold/15 transition-colors">
              <div className="flex-shrink-0 w-14 text-center">
                <p className="text-xs text-vip-gold font-semibold">{r.gelis_tarihi?.split('-')[2]}</p>
                <p className="text-[10px] text-gray-500">{r.gelis_saati?.slice(0,5)}</p>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-white font-medium truncate">{r.musteri_adi}</p>
                <p className="text-xs text-gray-400 truncate">{r.ilce} — {r.kisi_sayisi} kişi</p>
              </div>
              <div className="flex items-center gap-1">
                <span className={`text-[10px] px-2 py-1 rounded-md border ${DURUM_RENKLERI[r.durum] || 'bg-gray-500/20 text-gray-400 border-gray-500/30'}`}>{r.durum}</span>
                <a href={`/rezervasyonlar/yeni?edit=${r.id}`} className="w-6 h-6 rounded-md hover:bg-vip-gold/10 text-gray-600 hover:text-vip-gold transition-colors flex items-center justify-center"><i className="ri-edit-line text-[10px]"></i></a>
              </div>
            </div>
          ))}
          {upcomingRez.length === 0 && <p className="text-sm text-gray-500 text-center py-4">Yaklaşan rezervasyon yok</p>}
        </div>
      </div>
    </AdminLayout>
  );
}